import { useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { format, isToday, isWeekend } from "date-fns";
import { ArrowRight, CalendarOff, Clock } from "lucide-react";

import { useClickOutside } from "components/ui/DataTable/hooks/useClickOutside";
import { useDropdownPosition } from "components/ui/DataTable/hooks/useDropdownPosition";
import { pathGenerators } from "utils/routes";

import {
  buildDayAgenda,
  describeFreeTime,
  describeTimeOff,
  getServiceName,
} from "../../utils/build-day-agenda";
import styles from "./MonthView.module.css";

const MAX_VISIBLE_APPOINTMENTS = 3;
const AGENDA_WIDTH = 320;
const TIME_OFF_COLOR = "#c2410c";

const AGENDA_ROW_CLASS = {
  booked: styles.agendaRowBooked,
  off: styles.agendaRowOff,
  free: styles.agendaRowFree,
};

const getPatientName = (meeting) => {
  const patient = meeting.consultation?.patient;

  return patient ? `${patient.firstName} ${patient.surname}` : "Unknown patient";
};

// The compact month-grid chip has no separate line for the patient, unlike
// the agenda popover — so unlike getServiceName, fall back to the patient's
// name rather than a generic "Appointment" repeated on every chip that has
// no named service.
const getChipLabel = (meeting) => {
  const serviceName = meeting.consultation?.practiceService?.name || meeting.consultation?.serviceName;

  return serviceName || getPatientName(meeting);
};

const getTimeOffReason = (meeting) =>
  meeting.exception?.description || meeting.exception?.reason || "Time off";

// One day of the month grid: a compact summary (timeline, time-off/free-time
// line, a handful of appointment chips) plus a full-day agenda popover,
// opened by clicking the cell — the full breakdown a consultant actually
// wants without leaving the month view. A "View day" action inside the
// popover is what now jumps to the day view; a day with nothing to show
// (doctor not scheduled at all) still jumps straight there on click.
export const DayCell = ({
  date,
  doctor,
  onSelectDate,
  locationById,
  showLocations,
  serviceByName,
  showServices,
}) => {
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const triggerRef = useRef(null);
  const popoverRef = useRef(null);

  const { position, updatePosition } = useDropdownPosition(triggerRef, isDetailsOpen, {
    align: "left",
    menuWidth: AGENDA_WIDTH,
    menuRef: popoverRef,
  });

  useClickOutside(isDetailsOpen, triggerRef, popoverRef, () => setIsDetailsOpen(false));

  const agenda = buildDayAgenda(doctor);
  const bookedBlocks = agenda.filter((block) => block.kind === "booked");
  const timeOffLabel = describeTimeOff(agenda);
  const freeTimeLabel = describeFreeTime(agenda);
  const hasContent = bookedBlocks.length > 0 || !!timeOffLabel;
  const isCurrentDay = isToday(date);
  const isCurrentDayOff = isCurrentDay && !!timeOffLabel;

  const practiceStart = doctor?.practiceHours?.start ? new Date(doctor.practiceHours.start) : null;
  const practiceEnd = doctor?.practiceHours?.end ? new Date(doctor.practiceHours.end) : null;
  const totalMs = practiceStart && practiceEnd ? practiceEnd - practiceStart : 0;
  const toPercent = (blockDate) =>
    totalMs > 0 ? Math.min(100, Math.max(0, ((blockDate - practiceStart) / totalMs) * 100)) : 0;

  const visibleAppointments = bookedBlocks.slice(0, MAX_VISIBLE_APPOINTMENTS);
  const hiddenCount = bookedBlocks.length - visibleAppointments.length;

  // Only colour-code by service once there's an actual mix to distinguish —
  // with a single service in play everywhere, a borrowed colour would read
  // no differently than the plain default it replaces.
  const getServiceColor = (meeting) =>
    showServices ? serviceByName?.get(getServiceName(meeting))?.color : undefined;

  // One small pill per category present that day — one per distinct service
  // plus one for time off — so the header shows the day's mix at a glance
  // without opening the agenda. A pill only has room for the count; the
  // service name is a title tooltip, revealed on hover.
  const buildDayPills = () => {
    const countByServiceName = new Map();

    bookedBlocks.forEach((block) => {
      const serviceName = getServiceName(block.meeting);

      countByServiceName.set(serviceName, (countByServiceName.get(serviceName) ?? 0) + 1);
    });

    const servicePills = [...countByServiceName.entries()].map(([serviceName, count]) => ({
      key: serviceName,
      label: serviceName,
      count,
      color: (showServices && serviceByName?.get(serviceName)?.color) || "var(--green-700)",
    }));

    const offCount = agenda.filter((block) => block.kind === "off").length;
    const offPill = offCount > 0 ? [{ key: "off", label: "Time off", count: offCount, color: TIME_OFF_COLOR }] : [];

    return [...servicePills, ...offPill];
  };

  const dayPills = buildDayPills();

  // Nothing to show for a day the doctor isn't scheduled on at all — go
  // straight to the day view, same as clicking used to do everywhere.
  // Otherwise a click opens the agenda popover instead of navigating away.
  const handleCellClick = () => {
    if (agenda.length === 0) {
      onSelectDate(date);
      return;
    }

    if (!isDetailsOpen) updatePosition();
    setIsDetailsOpen((previous) => !previous);
  };

  const handleViewDayClick = (event) => {
    event.stopPropagation();
    setIsDetailsOpen(false);
    onSelectDate(date);
  };

  return (
    <div
      ref={triggerRef}
      className={`${styles.dayCell} ${isCurrentDay ? styles.dayCellToday : ""} ${
        isCurrentDayOff ? styles.dayCellTodayOff : ""
      } ${isWeekend(date) ? styles.dayCellWeekend : ""} ${
        hasContent ? styles.dayCellActive : ""
      }`}
      role="button"
      tabIndex={0}
      onClick={handleCellClick}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          handleCellClick();
        }
      }}
    >
      <div className={styles.dayCellHeader}>
        <span className={styles.dayNumber}>{date.getDate()}</span>
        {dayPills.length > 0 && (
          <div className={styles.dayPills}>
            {dayPills.map((pill) => (
              <span
                key={pill.key}
                className={styles.dayPill}
                style={{ background: pill.color }}
                title={pill.label}
              >
                {pill.count}
              </span>
            ))}
          </div>
        )}
      </div>

      {agenda.length > 0 && (
        <div className={styles.timeline}>
          {agenda
            .filter((block) => block.kind !== "free")
            .map((block, index) => (
              <div
                key={index}
                className={block.kind === "booked" ? styles.timelineBooked : styles.timelineOff}
                style={{
                  left: `${toPercent(block.start)}%`,
                  width: `${Math.max(toPercent(block.end) - toPercent(block.start), 2)}%`,
                }}
              />
            ))}
        </div>
      )}

      {!!timeOffLabel && (
        <span className={styles.timeOffLabel}>
          <CalendarOff size={11} />
          {timeOffLabel}
        </span>
      )}
      {!timeOffLabel && !!freeTimeLabel && (
        <span className={styles.freeTimeLabel}>
          <Clock size={11} />
          {freeTimeLabel}
        </span>
      )}

      <div className={styles.appointmentsList}>
        {visibleAppointments.map((block) => {
          const patient = block.meeting.consultation?.patient;
          const location = locationById?.get(block.meeting.locationId);
          const serviceColor = getServiceColor(block.meeting);

          return (
            <Link
              key={block.meeting.consultation.id}
              to={patient?.id ? pathGenerators.patient.consult(patient.id) : "#"}
              className={styles.appointmentChip}
              style={serviceColor ? { borderLeftColor: serviceColor } : undefined}
              onClick={(event) => event.stopPropagation()}
            >
              {!!showLocations && (
                <span
                  className={styles.locationDot}
                  style={{ background: location?.color || "var(--gray-300)" }}
                  title={location?.name}
                />
              )}
              <span className={styles.appointmentTime}>
                {format(block.start, "HH:mm")}–{format(block.end, "HH:mm")}
              </span>
              <span className={styles.appointmentService}>
                {getChipLabel(block.meeting)}
              </span>
            </Link>
          );
        })}

        {hiddenCount > 0 && (
          <span className={styles.moreLabel}>+{hiddenCount} more</span>
        )}
      </div>

      {isDetailsOpen &&
        agenda.length > 0 &&
        createPortal(
          <div
            ref={popoverRef}
            role="dialog"
            aria-label={`Agenda for ${format(date, "EEEE, d MMMM")}`}
            className={styles.agendaCard}
            style={{ position: "fixed", top: position.top, left: position.left, width: AGENDA_WIDTH }}
          >
            <div className={styles.agendaHeader}>
              <span className={styles.agendaHeaderDate}>
                {format(date, "EEEE, d MMMM")}
                {isCurrentDay && <span className={styles.agendaTodayBadge}>Today</span>}
              </span>
              <button
                type="button"
                className={styles.agendaViewDayButton}
                onClick={handleViewDayClick}
              >
                View day
                <ArrowRight size={12} />
              </button>
            </div>

            <div className={styles.agendaList}>
              {agenda.map((block, index) => {
                const timeRange = `${format(block.start, "HH:mm")}–${format(block.end, "HH:mm")}`;
                const rowClassName = AGENDA_ROW_CLASS[block.kind];

                if (block.kind === "free") {
                  return (
                    <div key={index} className={`${styles.agendaRow} ${rowClassName}`}>
                      <span className={styles.agendaTime}>{timeRange}</span>
                      <span className={styles.agendaDetail}>Free</span>
                    </div>
                  );
                }

                if (block.kind === "off") {
                  return (
                    <div key={index} className={`${styles.agendaRow} ${rowClassName}`}>
                      <span className={styles.agendaTime}>{timeRange}</span>
                      <span className={styles.agendaDetail}>
                        {getTimeOffReason(block.meeting)}
                      </span>
                    </div>
                  );
                }

                const patient = block.meeting.consultation?.patient;
                const location = locationById?.get(block.meeting.locationId);
                const serviceColor = getServiceColor(block.meeting);

                return (
                  <Link
                    key={index}
                    to={patient?.id ? pathGenerators.patient.consult(patient.id) : "#"}
                    className={`${styles.agendaRow} ${rowClassName}`}
                    style={serviceColor ? { borderLeftColor: serviceColor } : undefined}
                    onClick={(event) => event.stopPropagation()}
                  >
                    <span className={styles.agendaTime}>{timeRange}</span>
                    <span className={styles.agendaDetail}>
                      <span className={styles.agendaService}>
                        {getServiceName(block.meeting)}
                      </span>
                      <span className={styles.agendaPatient}>
                        {getPatientName(block.meeting)}
                      </span>
                      {!!showLocations && !!location && (
                        <span className={styles.agendaLocation}>
                          <span
                            className={styles.locationDot}
                            style={{ background: location.color }}
                          />
                          {location.name}
                        </span>
                      )}
                    </span>
                  </Link>
                );
              })}
            </div>
          </div>,
          document.body
        )}
    </div>
  );
};
